import { Controller } from "@hotwired/stimulus"

// Kontroler kalendarza urlopów (Organize::EmployeeLeave).
// - kliknięcie pustego dnia -> redirect na /organize/leaves/new z prefillem ?from=YYYY-MM-DD
// - kliknięcie bloku urlopu -> popover z detalami (rodzaj, status, godziny, zatwierdzający)
// - "więcej urlopów" na dniu -> popover z pełną listą urlopów tego dnia
// Analogiczny do task_calendar, ale uproszczony (bez widoku tygodniowego, bez recurring, bez priorytetów).
export default class extends Controller {
  static targets = [
    "dayEventsPopover", "dayEventsPanel", "dayEventsTitle", "dayEventsList",
    "eventDetail", "eventDetailPanel", "eventDetailName", "eventDetailKind", "eventDetailColor",
    "eventDetailDates", "eventDetailStatus", "eventDetailHours",
    "eventDetailTeamRow", "eventDetailTeam",
    "eventDetailApproverRow", "eventDetailApprover",
    "eventDetailDesc", "eventDetailShowLink", "eventDetailEditLink",
  ]

  static values = {
    newLeaveUrl: String,
    leavesById: Object,
    leavesByDate: Object,
    selectedDate: String,
  }

  // Kliknięcie pustego dnia - redirect do /organize/leaves/new z prefillem daty rozpoczęcia.
  openNewLeave(event) {
    if (event.target !== event.currentTarget) return

    const date = event.currentTarget.dataset.date
    if (!date) return

    const url = new URL(this.newLeaveUrlValue, window.location.origin)
    url.searchParams.set("from", date)
    window.location.href = url.toString()
  }

  // Mobile: zaznacz dzień.
  selectDay(event) {
    const button = event.currentTarget
    this.element.querySelectorAll("button[data-is-selected]").forEach(btn => {
      btn.removeAttribute("data-is-selected")
    })
    button.setAttribute("data-is-selected", "")
    this.selectedDateValue = button.dataset.date
  }

  // Popover z pełną listą urlopów danego dnia (po kliknięciu "+N więcej").
  showDayEvents(event) {
    event.stopPropagation()
    const date = event.currentTarget.dataset.date
    const dayData = this.leavesByDateValue[date] || {}
    const ids = dayData.ids || []
    const leaves = ids.map(id => this.leavesByIdValue[id]).filter(Boolean)

    this.dayEventsTitleTarget.textContent = dayData.title || date

    const items = leaves.map(ev => {
      const opacityClass = ev.status_key === "rejected" ? "line-through opacity-70" : ""
      return `<li>
        <button type="button" data-leave-id="${ev.id}" data-action="click->leave-calendar#showEventDetail" class="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 w-full text-left ${opacityClass}">
          <span class="size-2 shrink-0 rounded-full" style="background-color: ${this._escapeHtml(ev.color)};"></span>
          <span class="flex-auto truncate text-sm font-medium text-gray-900 group-hover:text-blue-600">${this._escapeHtml(ev.name)}</span>
          <span class="shrink-0 text-xs text-gray-500">${this._escapeHtml(ev.kind)}</span>
        </button>
      </li>`
    }).join("")
    this.dayEventsListTarget.innerHTML = items

    this.dayEventsPopoverTarget.classList.remove("hidden")
    this._positionPanel(this.dayEventsPanelTarget, event.currentTarget.getBoundingClientRect(), { width: 384, placement: "below" })
  }

  closeDayEvents() {
    this.dayEventsPopoverTarget.classList.add("hidden")
  }

  // Popover z detalami pojedynczego urlopu.
  showEventDetail(event) {
    event.stopPropagation()
    if (!this.hasEventDetailTarget) return

    const leaveId = event.currentTarget.dataset.leaveId
    const data = this.leavesByIdValue[leaveId]
    if (!data) return

    this.eventDetailNameTarget.textContent = data.name || ""
    this.eventDetailKindTarget.textContent = data.kind || ""
    this.eventDetailColorTarget.style.backgroundColor = data.color || "#3b82f6"

    const datesLabel = data.from === data.to ? data.from : `${data.from} - ${data.to}`
    this.eventDetailDatesTarget.textContent = `${datesLabel} (${data.days})`
    this.eventDetailStatusTarget.textContent = data.status || ""
    this.eventDetailHoursTarget.textContent = `${data.hours} h`

    this.eventDetailShowLinkTarget.href = data.url || "#"
    this.eventDetailEditLinkTarget.href = data.edit_url || "#"

    this._toggleRow("Team", data.team)
    this._toggleRow("Approver", data.approver)

    if (data.description) {
      this.eventDetailDescTarget.textContent = data.description
      this.eventDetailDescTarget.classList.remove("hidden")
    } else {
      this.eventDetailDescTarget.classList.add("hidden")
    }

    this.eventDetailTarget.classList.remove("hidden")
    this._positionPanel(this.eventDetailPanelTarget, event.currentTarget.getBoundingClientRect(), { width: 288, placement: "right" })
  }

  closeEventDetail() {
    if (this.hasEventDetailTarget) {
      this.eventDetailTarget.classList.add("hidden")
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Pozycjonuje panel popovera względem elementu triggera.
  // placement: "below" - pod triggerem, wycentrowany; "right" - z prawej strony triggera.
  // Automatycznie flipuje na przeciwną stronę jeśli nie mieści się w viewport.
  _positionPanel(panel, rect, { width, placement }) {
    let left, top

    if (placement === "below") {
      left = rect.left + rect.width / 2 - width / 2
      top = rect.bottom + 4
      if (top + panel.offsetHeight > window.innerHeight - 8) {
        top = rect.top - panel.offsetHeight - 4
      }
    } else {
      left = rect.right + 8
      top = rect.top
      if (left + width > window.innerWidth - 8) {
        left = rect.left - width - 8
      }
    }

    if (left < 8) left = 8
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8

    panel.style.left = `${left}px`
    panel.style.top = `${top}px`

    if (placement === "right") {
      requestAnimationFrame(() => {
        let adjustedTop = top
        if (adjustedTop + panel.offsetHeight > window.innerHeight - 8) {
          adjustedTop = window.innerHeight - panel.offsetHeight - 8
        }
        if (adjustedTop < 8) adjustedTop = 8
        panel.style.top = `${adjustedTop}px`
      })
    }
  }

  _toggleRow(suffix, value) {
    const rowKey = `eventDetail${suffix}Row`
    const valKey = `eventDetail${suffix}`
    const hasRow = this[`has${rowKey.charAt(0).toUpperCase() + rowKey.slice(1)}Target`]
    const hasVal = this[`has${valKey.charAt(0).toUpperCase() + valKey.slice(1)}Target`]
    if (!hasRow || !hasVal) return
    const rowEl = this[`${rowKey}Target`]
    const valEl = this[`${valKey}Target`]
    if (value) {
      valEl.textContent = value
      rowEl.classList.remove("hidden")
    } else {
      rowEl.classList.add("hidden")
    }
  }

  _escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
